import { useCallback, useEffect, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { CurrencyCode } from '@core/utils/currency'
import { httpErrorText } from '@core/utils/httpError'
import { cx, hint } from '@ui/classes'
import { credInput, errBanner, okBanner } from '@ui/admin'
import { CLOUD_API } from './shared'
import { useAuthToken } from './useAuthToken'

/**
 * «Политика кассы»: добавлять ли неизвестные товары на лету и валюта магазина.
 * Перенесено 1:1 из прежнего инлайн-блока AdminPage.
 */
export function CashierPolicySection({ core }: { core: AppCore }) {
  const { catalog } = core
  const token = useAuthToken()
  const [allowOnTheFly, setAllowOnTheFly] = useState(true)
  const [frequentEnabled, setFrequentEnabled] = useState(false)
  const [currency, setCurrency] = useState<CurrencyCode>('RUB')
  const [lowStock, setLowStock] = useState('3')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [onTheFly, frequent, cur, threshold] = await Promise.all([
        catalog.isOnTheFlyAllowed(),
        catalog.isFrequentProductsEnabled(),
        catalog.getStoreCurrency(),
        catalog.getLowStockThreshold()
      ])
      setAllowOnTheFly(onTheFly)
      setFrequentEnabled(frequent)
      setCurrency(cur)
      setLowStock(String(threshold))

      // Облако — источник истины для настроек организации (ARCH-TASK-64a).
      // Офлайн остаёмся на локальном кэше.
      if (token) {
        try {
          const response = await fetch(`${CLOUD_API}/tenant/settings`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (response.ok) {
            const json = (await response.json()) as {
              settings?: { lowStockThreshold?: number }
            }
            const remote = Number(json.settings?.lowStockThreshold)
            if (Number.isFinite(remote) && remote >= 0 && remote !== threshold) {
              await catalog.setLowStockThreshold(remote)
              setLowStock(String(remote))
            }
          }
        } catch {
          // Нет связи — используем локальный кэш.
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [catalog, token])

  useEffect(() => {
    void load()
  }, [load])

  /** Мгновенно сохранить валюту магазина в Dexie meta. */
  const changeCurrency = async (next: CurrencyCode) => {
    setCurrency(next)
    try {
      await catalog.setStoreCurrency(next)
      setMessage('Валюта магазина: ' + next)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /** Включить/выключить блок «Часто покупают» на кассе. */
  const toggleFrequent = async (next: boolean) => {
    setFrequentEnabled(next)
    try {
      await catalog.setFrequentProductsEnabled(next)
      setMessage(
        next
          ? 'Быстрые товары включены: блок «Часто покупают» виден на кассе'
          : 'Быстрые товары выключены: блок «Часто покупают» скрыт'
      )
    } catch (err: unknown) {
      setFrequentEnabled(!next)
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /** Мгновенно сохранить право «добавлять товары на лету». */
  const toggleOnTheFly = async (next: boolean) => {
    setAllowOnTheFly(next)
    try {
      await catalog.setOnTheFlyAllowed(next)
      setMessage(
        next
          ? 'Кассирам разрешено добавлять товары на лету'
          : 'Добавление товаров на лету запрещено'
      )
    } catch (err: unknown) {
      setAllowOnTheFly(!next)
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /** Сохранить порог «товар заканчивается» (по blur/Enter). */
  const saveLowStock = async () => {
    const parsed = Number(lowStock.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError('Порог должен быть неотрицательным числом')
      return
    }
    try {
      const current = await catalog.getLowStockThreshold()
      if (current === parsed) return
      // Сначала облако (настройка общая для сети), затем локальный кэш.
      if (token) {
        const response = await fetch(`${CLOUD_API}/tenant/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ lowStockThreshold: parsed })
        })
        if (!response.ok) {
          throw new Error(httpErrorText('Настройки организации', response.status))
        }
      }
      await catalog.setLowStockThreshold(parsed)
      setLowStock(String(parsed))
      setMessage(
        token
          ? `Порог «заканчивается»: ${parsed} ед. — сохранён для организации`
          : `Порог «заканчивается»: ${parsed} ед. (только на этой кассе)`
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div>
      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <label className="flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={allowOnTheFly}
          onChange={(e) => void toggleOnTheFly(e.target.checked)}
         className="h-4 w-4 cursor-pointer accent-ok"/>
        <span>Разрешить кассирам добавлять неизвестные товары на кассе</span>
      </label>
      <p className={hint}>
        Если выключено — при сканировании неизвестного штрихкода кассир увидит только «Товар не
        найден», без кнопки создания.
      </p>

      <label className="mt-2.5 flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={frequentEnabled}
          onChange={(e) => void toggleFrequent(e.target.checked)}
          className="h-4 w-4 cursor-pointer accent-ok"
        />
        <span>Быстрые товары (часто покупают)</span>
      </label>
      <p className={hint}>
        Показывает на кассе блок «Часто покупают» с товарами из истории продаж за 7 дней.
        Выключено — блок скрыт, плитки категорий занимают всё окно.
      </p>

      <label className="mt-2.5 flex items-center gap-2.5">
        <span>Валюта магазина:</span>
        <select
          value={currency}
          onChange={(e) => void changeCurrency(e.target.value as CurrencyCode)}
         className={credInput}>
          <option value="RUB">RUB (₽)</option>
        </select>
      </label>
      <p className={hint}>
        Версия для рынка РФ. UZ-режим (сум, Asl Belgisi, Click/Payme) вынесен в
        отдельную сборку для Узбекистана.
      </p>

      <label className="mt-2.5 flex items-center gap-2.5">
        <span>Порог «заканчивается», ед.:</span>
        <input
          type="number"
          min={0}
          step="0.5"
          value={lowStock}
          onChange={(e) => setLowStock(e.target.value)}
          onBlur={() => void saveLowStock()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void saveLowStock()
          }}
          className={cx(credInput, 'w-24 text-right')}
        />
      </label>
      <p className={hint}>
        Товары с остатком не больше этого значения попадают в блок «Заканчиваются» в «Обзоре».
        Учитываются единицы товара: для штучных — штуки, для весовых — килограммы.
      </p>
    </div>
  )
}
